/*
June 2026

Encapsulates experiment validation, toast notifications, and experiment configuration creation.
*/

import * as React from "react";

import { Button } from "primereact/button";
import { Toast } from "primereact/toast";

import { getLevelBounds } from "./FactorialLevels";

/**
 * Validates a single distribution-shaped object (dist/min/max/arg3/arg4).
 * Shared by Univariate/Monte Carlo parameters and Factorial's distribution
 * levels.
 */
function validateDistribution(dist) {
  if (dist.dist === "uniform") {
    if (dist.min === null || dist.max === null) {
      return "Uniform distribution requires both min and max values.";
    } else if (dist.min >= dist.max) {
      return "Min must be lower than max.";
    }
  } else if (dist.dist === "triangular") {
    if (dist.min === null || dist.max === null || dist.arg3 === null) {
      return "Triangular distribution requires min, max, and mode values.";
    } else if (dist.min >= dist.max) {
      return "Min must be lower than max.";
    } else if (dist.arg3 < dist.min || dist.arg3 > dist.max) {
      return "Mode must be between max & min.";
    }
  } else if (dist.dist === "exponential") {
    if (dist.arg3 === null) {
      return "Exponential distribution requires lambda.";
    } else if (dist.arg3 <= 0) {
      return "Lambda value must be positive.";
    }
  } else if (dist.dist === "poisson") {
    if (dist.arg3 === null) {
      return "Poisson distribution requires lambda.";
    } else if (dist.arg3 <= 0) {
      return "Lambda value must be positive.";
    }
  } else if (dist.dist === "normal") {
    if (dist.arg3 === null || dist.arg4 === null) {
      return "Normal distribution requires mean and standard deviation.";
    } else if (dist.arg3 < 0) {
      return "Mean must be positive.";
    } else if (dist.arg4 <= 0) {
      return "Standard deviation must be greater than 0.";
    }
  } else if (dist.dist === "erlang") {
    if (dist.arg3 === null || dist.arg4 === null) {
      return "Erlang distribution requires shape and scale values.";
    } else if (dist.arg3 <= 0) {
      return "Shape must be positive.";
    } else if (dist.arg4 <= 0) {
      return "Scale must be positive.";
    }
  }

  return null;
}

function ConfigValidation({
  config,
  configWarnings,
  selectedParameters,
  simulateDiagram,
  isLoading,
  numLevels,
  isDistributionOverlayOpen,
  cancelRun,
}) {
  const toastRef = React.useRef(null);
  const [visible, setVisible] = React.useState(false);

  const verifyExperiment = React.useCallback(() => {
    const warnings = {};

    if (config.experiment.type === "None") {
      return warnings;
    }

    if (config.experiment.type === "Factorial") {
      // Factorial rows carry a `values` array (one entry per level). Each
      // level is either a constant number, or a distribution-shaped object
      // (same shape as Univariate/Monte Carlo)
      for (const param of selectedParameters) {
        const values = param.values || [];
        if (values.length === 0) {
          warnings[param.displayName] =
            "Factorial parameter requires a value for every level.";
          continue;
        }

        for (let i = 0; i < values.length; i++) {
          const level = values[i];
          const levelLabel = `Level ${i + 1}`;

          if (level === null || level === undefined || level === "") {
            warnings[param.displayName] = "All level values must be filled in.";
          } else if (typeof level === "object") {
            const warning = validateDistribution(level);
            if (warning) {
              warnings[param.displayName] = `${levelLabel}: ${warning}`;
            }
          }

          // Levels are meant to be ordered low to high (that's what the
          // "Low"/"Medium"/"High" labels promise). Two independently-sampled
          // distributions with overlapping ranges can invert that order on a
          // given trial, so bounded levels (constant, uniform, triangular)
          // get checked here.
          if (i > 0 && !warnings[param.displayName]) {
            const lower = getLevelBounds(values[i - 1]);
            const upper = getLevelBounds(level);
            if (lower && upper && lower.max >= upper.min) {
              warnings[param.displayName] =
                `Level ${i}/${levelLabel}: ranges overlap or are out of order.`;
            }
          }
        }
      }
    } else {
      for (const param of selectedParameters) {
        const warning = validateDistribution(param);
        if (warning) {
          warnings[param.displayName] = warning;
        }
      }
    }

    return warnings;
  }, [config.experiment.type, selectedParameters]);

  const verifyConfig = React.useCallback(() => {
    const systemEntries = Object.entries(configWarnings.system);
    const entityTypeEntries = Object.entries(configWarnings.entity_types);
    const actionEntries = Object.entries(configWarnings.actions);
    const resourceEntries = Object.entries(configWarnings.resources);
    const experimentEntries = Object.entries(verifyExperiment());

    const hasSystemWarnings = systemEntries.length > 0;
    const hasEntityTypeWarnings = entityTypeEntries.some(
      ([, fields]) => Object.keys(fields).length !== 0,
    );
    const hasActionWarnings = actionEntries.some(
      ([, fields]) => Object.keys(fields).length !== 0,
    );
    const hasResourceWarnings = resourceEntries.some(
      ([, fields]) => Object.keys(fields).length !== 0,
    );
    const hasExperimentWarnings = experimentEntries.length > 0;

    if (
      hasSystemWarnings ||
      hasEntityTypeWarnings ||
      hasActionWarnings ||
      hasResourceWarnings ||
      hasExperimentWarnings
    ) {
      if (!visible) {
        setVisible(true);
        toastRef.current?.clear();
        toastRef.current?.show({
          severity: "warn",
          summary: "Configuration Warnings",
          sticky: true,
          content: () => (
            <div>
              {hasSystemWarnings && (
                <div>
                  <h4>System Parameters</h4>
                  {systemEntries.map(([parameter, warning]) => (
                    <div key={parameter}>
                      <p>
                        <strong>{parameter}:</strong> {warning}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              {entityTypeEntries.map(([entityTypeId, fields]) => {
                if (Object.keys(fields).length === 0) {
                  return null;
                }
                return (
                  <div key={entityTypeId}>
                    <h4>Action: {entityTypeId}</h4>
                    {Object.values(fields).map((message, i) => (
                      <p key={i}>{message}</p>
                    ))}
                  </div>
                );
              })}
              {actionEntries.map(([actionId, fields]) => {
                if (Object.keys(fields).length === 0) {
                  return null;
                }
                return (
                  <div key={actionId}>
                    <h4>Action: {actionId}</h4>
                    {Object.values(fields).map((message, i) => (
                      <p key={i}>{message}</p>
                    ))}
                  </div>
                );
              })}
              {resourceEntries.map(([resourceId, fields]) => {
                if (Object.keys(fields).length === 0) {
                  return null;
                }
                return (
                  <div key={resourceId}>
                    <h4>Resource: {resourceId}</h4>
                    {Object.values(fields).map((message, i) => (
                      <p key={i}>{message}</p>
                    ))}
                  </div>
                );
              })}
              {experimentEntries.map(([displayName, warning]) => (
                <div key={displayName}>
                  <h4>Input: {displayName}</h4>
                  <p>{warning}</p>
                </div>
              ))}
            </div>
          ),
        });
      }
      return false;
    }

    return true;
  }, [configWarnings, verifyExperiment, visible]);

  const createExperimentConfig = React.useCallback(() => {
    if (config.experiment.type === "Factorial") {
      // Factorial trials are built from fixed per-level values, not a
      // distribution, so send `values` instead of dist/min/max/arg3/arg4.
      return selectedParameters.map((param) => ({
        gid: param.gid,
        name: param.name,
        type: param.type,
        parameter: param.parameter,
        param_arg: param.param_arg,
        responseVariable: param.responseVariable,
        values: (param.values || []).slice(0, numLevels),
      }));
    }

    return selectedParameters.map((param) => ({
      gid: param.gid,
      name: param.name,
      type: param.type,
      parameter: param.parameter,
      param_arg: param.param_arg,
      constant: param.constant,
      dist: param.dist,
      min: param.min,
      max: param.max,
      arg3: param.arg3,
      arg4: param.arg4,
    }));
  }, [selectedParameters, config.experiment.type, numLevels]);

  const clear = () => {
    toastRef.current?.clear();
    setVisible(false);
  };

  const startSimulation = () => {
    simulateDiagram(verifyConfig, createExperimentConfig);
  };

  return (
    <>
      <Toast ref={toastRef} onRemove={clear} position="center" />
      <Button
        label={
          isLoading
            ? "Running…"
            : config.experiment.type === "None"
              ? "Run Simulation"
              : "Run Experiment"
        }
        onClick={startSimulation}
        disabled={isLoading || isDistributionOverlayOpen}
        tooltip={
          isDistributionOverlayOpen
            ? "Close the distribution editor before running the simulation."
            : undefined
        }
        tooltipOptions={{ position: "top" }}
        style={{ backgroundColor: "#06b814" }}
      />
      {isLoading && (
        <Button
          label="Cancel"
          onClick={cancelRun}
          style={{ backgroundColor: "#b81414", marginLeft: "0.5rem" }}
        />
      )}
    </>
  );
}

export default ConfigValidation;
